import { useState, useEffect, type CSSProperties } from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { BarRole } from "../../types/auth";
import { listPourChecks } from "../../lib/api";
import CommandPalette from "../common/CommandPalette";

interface Props {
  barRoles: BarRole[];
  isMobile: boolean;
}

interface NavGroup {
  id: string;
  name: string;
  icon: string;
  badgeColor: string;
  links: { to: string; label: string; ownerOnly?: boolean; adminOnly?: boolean }[];
}

/** The one link that carries a live count: open rows of public.pour_checks. */
const BOTTLE_CHECKS_PATH = "/admin/bottle-checks";

const NAV_GROUPS: NavGroup[] = [
  {
    id: "service",
    name: "Floor & Service",
    icon: "🍸",
    badgeColor: "#27ae60",
    links: [
      { to: "/admin/shelf-map", label: "Shelf Bottle Map" },
      { to: BOTTLE_CHECKS_PATH, label: "Bottle Checks" },
      { to: "/admin/cash-management", label: "Cash Bank & Drawer" },
      { to: "/admin/operations", label: "Shift Checklists" },
      { to: "/admin/pick-list", label: "Floor Pick List" },
      { to: "/admin/events", label: "Events & Tickets" },
      { to: "/admin/recipes", label: "Cocktail Recipes", ownerOnly: true },
      { to: "/admin/prep-recipes", label: "Prep Recipes & Syrups", ownerOnly: true },
      { to: "/admin/ingredients", label: "Bar Ingredients", ownerOnly: true },
      { to: "/admin/cocktail-mapping", label: "Cocktail Pour Mapping", ownerOnly: true },
      { to: "/admin/tasting-mat", label: "Tasting Mats", ownerOnly: true },
      { to: "/admin/flights", label: "Flight Builder", ownerOnly: true },
    ],
  },
  {
    id: "inventory",
    name: "Inventory & Spirits",
    icon: "📦",
    badgeColor: "#F9AF36",
    links: [
      { to: "/admin/inventory", label: "Inventory Master" },
      { to: "/admin/inventory-count", label: "Cycle Count & Audit" },
      { to: "/admin/whiskey-intel", label: "Whiskey Intel & Reps" },
      { to: "/admin/enrichment", label: "Catalog AI Enrichment" },
      { to: "/admin/catalog-monitor", label: "Catalog Drift Monitor" },
      { to: "/admin/merge", label: "Merge Duplicate Bottles" },
      { to: "/admin/curation", label: "Curation & Media" },
    ],
  },
  {
    id: "guests",
    name: "Guests & C33",
    icon: "👥",
    badgeColor: "#9B59B6",
    links: [
      { to: "/admin/communications", label: "Unified Inbox & Softphone" },
      { to: "/admin/crm", label: "Guest CRM & Contacts" },
      { to: "/admin/members", label: "C33 Member Roster" },
      { to: "/admin/club-pours", label: "Club Pour Planner" },
      { to: "/admin/events", label: "Events & Check-In" },
      { to: "/admin/reviews", label: "Reviews (Guest & Google)" },
      { to: "/admin/bottle-photos", label: "Member Bottle Photos" },
      { to: "/admin/trails", label: "Whiskey Trails & Passports" },
      { to: "/admin/suggested-edits", label: "Suggested Edits" },
    ],
  },
  {
    id: "publishing",
    name: "Publishing & Growth",
    icon: "📢",
    badgeColor: "#4A8FD9",
    links: [
      { to: "/admin/articles", label: "Journal Articles & News" },
      { to: "/admin/templates", label: "Email & SMS Templates" },
      { to: "/admin/partners", label: "Brand Partners & Sponsors" },
      { to: "/admin/seo", label: "Search & Referrals" },
      { to: "/admin/charity", label: "S3 Giving Ledger" },
    ],
  },
  {
    id: "admin",
    name: "Intelligence & Admin",
    icon: "⚙️",
    badgeColor: "#E67E22",
    links: [
      { to: "/admin/dashboard", label: "KPI Dashboard" },
      { to: "/admin/business-context", label: "Business Demand Context" },
      { to: "/admin/knowledge", label: "Knowledge & Staff SOPs" },
      { to: "/admin/activity", label: "Audit Activity Log" },
      { to: "/admin/comp-audit", label: "Comp & Waste Audit" },
      { to: "/admin/issues", label: "Issue Reports" },
      { to: "/admin/settings/voice", label: "Voice Bot Settings" },
      { to: "/admin/import-export", label: "Import / Export" },
      { to: "/admin/settings", label: "General Settings" },
      { to: "/admin/staff-access", label: "Staff Access & Roles", ownerOnly: true },
      { to: "/admin/station-tablets", label: "Station Tablets", ownerOnly: true },
    ],
  },
];

export default function AdminNav({ barRoles, isMobile }: Props) {
  const isOwner = barRoles.includes("bar_owner");
  const isManager = barRoles.includes("bar_manager");
  const isManagerOrOwner = isManager || isOwner;

  // Bartenders only see Floor & Service tools
  const visibleNavGroups = isManagerOrOwner
    ? NAV_GROUPS
    : NAV_GROUPS.filter((g) => g.id === "service");

  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [openChecks, setOpenChecks] = useState(0);

  // Determine current active group based on URL
  const currentActiveGroupId = visibleNavGroups.find((g) =>
    g.links.some((l) => location.pathname === l.to)
  )?.id || null;

  // Single open group accordion: only one group can be open at a time. On mobile the sheet
  // opens with the current page's group, or the first group, already expanded.
  const [openGroupId, setOpenGroupId] = useState<string | null>(
    () => currentActiveGroupId ?? (isMobile ? visibleNavGroups[0]?.id ?? null : null)
  );

  // Auto-switch open group when route changes
  useEffect(() => {
    if (currentActiveGroupId) {
      setOpenGroupId(currentActiveGroupId);
    }
  }, [location.pathname]);

  // Open bottle checks, for the badge. Fetched once on mount and again whenever the user enters
  // or leaves the Bottle Checks screen — the only moment their own actions can have changed it.
  // A failure is swallowed: a missing badge is not worth breaking the navigation over.
  const onBottleChecks = location.pathname === BOTTLE_CHECKS_PATH;
  useEffect(() => {
    let cancelled = false;
    listPourChecks()
      .then((rows) => {
        if (!cancelled) setOpenChecks(rows.length);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [onBottleChecks]);

  const badgeFor = (to: string) => (to === BOTTLE_CHECKS_PATH && openChecks > 0 ? openChecks : null);

  // Global shortcut listener for ⌘K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const toggleGroup = (id: string) => {
    setOpenGroupId((prev) => (prev === id ? null : id));
  };

  const isLinkAllowed = (l: { to: string; label: string; ownerOnly?: boolean; adminOnly?: boolean }) => {
    if (l.ownerOnly && !isOwner) return false;
    if (l.adminOnly && !isManagerOrOwner) return false;
    return true;
  };

  if (isMobile) {
    // Vertical accordion: one full-width row per tool, grouped by domain. Scrolls up and down
    // inside the sheet — no sideways strips. A bartender sees a single group, so it stays open.
    const singleGroup = visibleNavGroups.length === 1;

    return (
      <>
        <div style={styles.mobileNavContainer}>
          <button
            type="button"
            style={styles.mobileSearchBtn}
            onClick={() => setPaletteOpen(true)}
          >
            <span>🔍 Search Station Tools…</span>
            <kbd style={styles.mobileKbd}>⌘K</kbd>
          </button>

          <nav aria-label="Admin Navigation" style={styles.mobileGroups}>
            {visibleNavGroups.map((group) => {
              const isOpen = singleGroup || openGroupId === group.id;
              const activeInside = group.links.some((l) => location.pathname === l.to);
              const groupLinks = group.links.filter(isLinkAllowed);
              const groupBadge = groupLinks.reduce((n, l) => n + (badgeFor(l.to) ?? 0), 0);
              const panelId = `admin-nav-group-${group.id}`;

              return (
                <section key={group.id} style={styles.mobileGroup}>
                  {!singleGroup && (
                    <button
                      type="button"
                      style={{
                        ...styles.mobileGroupHeader,
                        color: activeInside ? "var(--gold)" : "var(--text)",
                      }}
                      onClick={() => toggleGroup(group.id)}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                    >
                      <span style={styles.mobileGroupIcon}>{group.icon}</span>
                      <span style={styles.mobileGroupTitle}>{group.name}</span>
                      {!isOpen && groupBadge > 0 && <span style={styles.navBadge}>{groupBadge}</span>}
                      <span style={styles.groupCount}>{groupLinks.length}</span>
                      <span style={styles.groupChevron}>{isOpen ? "▾" : "▸"}</span>
                    </button>
                  )}

                  {isOpen && (
                    <div id={panelId} style={styles.mobileGroupLinks}>
                      {groupLinks.map((l) => (
                        <NavLink
                          key={l.to}
                          to={l.to}
                          style={({ isActive }) => ({
                            ...styles.mobileLink,
                            background: isActive ? "rgba(249,175,54,0.14)" : "transparent",
                            color: isActive ? "var(--gold)" : "var(--text)",
                            borderColor: isActive ? "rgba(249,175,54,0.4)" : "transparent",
                            fontWeight: isActive ? 700 : 500,
                          })}
                        >
                          <span style={{ flex: 1 }}>{l.label}</span>
                          {l.ownerOnly && <span style={styles.linkBullet} aria-label="Owner only">🔒</span>}
                          {badgeFor(l.to) !== null && (
                            <span style={styles.navBadge} aria-label={`${badgeFor(l.to)} open`}>
                              {badgeFor(l.to)}
                            </span>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </nav>
        </div>

        <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
      </>
    );
  }

  // Desktop Grouped Sidebar
  return (
    <>
      <nav style={styles.desktopRail} className="no-scrollbar" aria-label="Admin Navigation">
        {/* Search / Command Palette Button */}
        <button
          type="button"
          style={styles.searchTriggerBtn}
          onClick={() => setPaletteOpen(true)}
          title="Open Quick Search (⌘K)"
        >
          <span style={{ fontSize: 13 }}>🔍</span>
          <span style={{ flex: 1, textAlign: "left", fontSize: 12, color: "var(--muted)" }}>Quick Search…</span>
          <kbd style={styles.kbd}>⌘K</kbd>
        </button>

        {/* Domain Groups */}
        <div style={styles.groupsContainer}>
          {visibleNavGroups.map((group) => {
            const isOpen = openGroupId === group.id;
            const activeInside = group.links.some((l) => location.pathname === l.to);
            const visibleGroupLinks = group.links.filter(isLinkAllowed);

            return (
              <div key={group.id} style={styles.groupBlock}>
                <button
                  type="button"
                  style={{
                    ...styles.groupHeader,
                    color: activeInside ? "var(--gold)" : "var(--text)",
                  }}
                  onClick={() => toggleGroup(group.id)}
                >
                  <span style={styles.groupIcon}>{group.icon}</span>
                  <span style={styles.groupTitle}>{group.name}</span>
                  <span style={styles.groupCount}>({visibleGroupLinks.length})</span>
                  <span style={styles.groupChevron}>{isOpen ? "▾" : "▸"}</span>
                </button>

                {isOpen && (
                  <div style={styles.groupLinks}>
                    {visibleGroupLinks.map((l) => (
                      <NavLink
                        key={l.to}
                        to={l.to}
                        style={({ isActive }) => ({
                          ...styles.railLink,
                          background: isActive
                            ? "linear-gradient(90deg, rgba(249,175,54,0.14) 0%, rgba(249,175,54,0.04) 100%)"
                            : "transparent",
                          color: isActive ? "var(--gold)" : "var(--muted)",
                          borderColor: isActive ? "rgba(249,175,54,0.4)" : "transparent",
                          fontWeight: isActive ? 700 : 500,
                        })}
                      >
                        <span style={styles.linkBullet}>{l.ownerOnly ? "🔒" : "•"}</span>
                        <span style={{ flex: 1 }}>{l.label}</span>
                        {badgeFor(l.to) !== null && (
                          <span style={styles.navBadge} aria-label={`${badgeFor(l.to)} open`}>
                            {badgeFor(l.to)}
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}

const styles: Record<string, CSSProperties> = {
  navBadge: {
    marginLeft: 6,
    background: "#E05040",
    color: "#fff",
    borderRadius: 10,
    padding: "1px 7px",
    fontSize: 10,
    fontWeight: 800,
    lineHeight: "16px",
    display: "inline-block",
  },
  desktopRail: {
    width: 240,
    flexShrink: 0,
    padding: "14px 12px 24px",
    borderRight: "1px solid var(--border)",
    height: "100vh",
    position: "sticky",
    top: 0,
    overflowY: "auto",
    background: "var(--bg)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  searchTriggerBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "8px 10px",
    color: "var(--text)",
    cursor: "pointer",
    transition: "border-color 0.15s",
  },
  kbd: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    padding: "1px 5px",
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 700,
    color: "var(--muted)",
  },
  groupsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  groupBlock: {
    display: "flex",
    flexDirection: "column",
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: "none",
    padding: "6px 4px",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    borderRadius: 6,
    transition: "background 0.15s",
  },
  groupIcon: {
    fontSize: 13,
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    flex: 1,
  },
  groupCount: {
    fontSize: 10,
    color: "var(--muted)",
    opacity: 0.7,
  },
  groupChevron: {
    fontSize: 12,
    color: "var(--muted)",
    marginLeft: 4,
  },
  groupLinks: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    paddingLeft: 6,
    marginTop: 2,
    borderLeft: "1px solid rgba(255,255,255,0.06)",
    marginLeft: 8,
  },
  railLink: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 8px",
    borderRadius: 6,
    border: "1px solid transparent",
    fontSize: 12,
    textDecoration: "none",
    transition: "all 0.1s",
  },
  linkBullet: {
    fontSize: 10,
    opacity: 0.5,
  },
  mobileNavContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  mobileSearchBtn: {
    width: "100%",
    minHeight: 44,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "10px 12px",
    color: "var(--muted)",
    fontSize: 14,
    cursor: "pointer",
  },
  mobileKbd: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    padding: "2px 6px",
    borderRadius: 4,
    fontSize: 10,
    color: "var(--gold)",
    fontWeight: 700,
  },
  mobileGroups: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  mobileGroup: {
    display: "flex",
    flexDirection: "column",
    borderBottom: "1px solid var(--border)",
    paddingBottom: 4,
  },
  mobileGroupHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 48,
    width: "100%",
    padding: "8px 4px",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    touchAction: "manipulation",
  },
  mobileGroupIcon: {
    fontSize: 18,
    width: 24,
    textAlign: "center",
  },
  mobileGroupTitle: {
    flex: 1,
    fontSize: 13,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  mobileGroupLinks: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    paddingBottom: 6,
  },
  mobileLink: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minHeight: 44,
    padding: "10px 12px 10px 38px",
    borderRadius: 8,
    border: "1px solid",
    fontSize: 15,
    textDecoration: "none",
    touchAction: "manipulation",
  },
};
